// Apolo IDE — TopTabs (Code | Chat | Plan | Council | GOD | Hermes | Dispatch-mobile).
//
// Container glass + active tab com slide indicator Framer Motion `layoutId`.
// cursor-design (2026-07-03): a aba ativa é o padrão segmented NEUTRO —
// superfície sólida + borda fina + sombra leve (tokens --apolo-tab-active-*);
// o gradiente azul + multi-layer glow saíram do header. Raios do IDE do Cursor
// (Pedro: pill era redondo demais): track 8px (rounded-apolo-lg), chips 6px
// (rounded-apolo-md) — mesmo padrão do botão Adapters (8px).
// Mobile-first: touch target 44px, scrollbar hidden, gap reduzido.

import { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useAgentStore } from '../store/useAgentStore.js';
import { useViewport } from '../hooks/useViewport.js';
import { maturityBadge } from '../lib/surfaceMaturity.js';

const TABS = [
  // Code e Chat continuam como superfícies de compatibilidade enquanto a
  // sidebar unificada é migrada. Hermes deixa de ser um destino visual.
  { id: 'code', label: 'Code' },
  { id: 'chat', label: 'Chat' },
  { id: 'plan', label: 'Plan' },
  { id: 'council', label: 'Council' },
  { id: 'god', label: 'GOD' },
  // Dispatch é client-side de "rodar no Mac" — só faz sentido no celular
  // (no desktop você já está na máquina). mobileOnly esconde no desktop.
  { id: 'dispatch', label: 'Dispatch', mobileOnly: true },
];

// Spring tight — quero responsiveness sem bounce excessivo no slide.
const SPRING = { type: 'spring', stiffness: 380, damping: 32, mass: 0.6 };

export default function TopTabs() {
  const active = useAgentStore((s) => s.activeWorkspace);
  const setActive = useAgentStore((s) => s.setActiveWorkspace);
  const reduce = useReducedMotion();
  const { isMobile } = useViewport();
  const visibleTabs = TABS.filter((tab) => !tab.mobileOnly || isMobile);
  const scrollRef = useRef(null);
  const activeRef = useRef(null);
  const didCenterRef = useRef(false);

  // iPhone: o strip de abas estoura a largura. Mantém a aba ativa centrada e
  // visível (sem isso a ativa pode ficar fora da tela e as outras "somem").
  // - rAF: mede DEPOIS do layout assentar (no mount, offsetLeft/clientWidth
  //   ainda não estão prontos → scroll caía em 0).
  // - 1º run (mount) é INSTANT: smooth no mount é cancelado pelo re-layout do
  //   framer-motion highlight → a ativa ficava fora da tela. Trocas de aba
  //   depois usam smooth (UX).
  // - fonts.ready: a web font muda a largura das abas → re-centra ao carregar.
  useEffect(() => {
    const center = (instant) => {
      const c = scrollRef.current;
      const el = activeRef.current;
      if (!c || !el) return;
      const target = el.offsetLeft - (c.clientWidth - el.offsetWidth) / 2;
      c.scrollTo({
        left: Math.max(0, target),
        behavior: instant || reduce ? 'auto' : 'smooth',
      });
    };
    let cancelled = false;
    const firstRun = !didCenterRef.current;
    didCenterRef.current = true;
    const raf = requestAnimationFrame(() => center(firstRun));
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!cancelled) center(true);
      });
    }
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [active, reduce, isMobile]);

  return (
    <div
      ref={scrollRef}
      role="tablist"
      aria-label="Workspaces"
      className="mx-auto flex w-fit max-w-full items-center gap-1.5 overflow-x-auto scroll-px-3 rounded-apolo-lg border border-apolo-borderFocus bg-apolo-glass p-1 backdrop-blur-md sm:max-w-none sm:gap-2"
      style={{
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {visibleTabs.map((tab) => {
        const isActive = active === tab.id;
        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            id={`tab-${tab.id}`}
            ref={isActive ? activeRef : null}
            onClick={() => setActive(tab.id)}
            className={`relative shrink-0 rounded-apolo-md px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/60 min-h-[44px] sm:min-h-0 sm:px-4 sm:py-1.5 sm:text-sm ${
              isActive
                ? 'text-apolo-textPrimary'
                : 'text-apolo-textTertiary hover:text-apolo-textSecondary dark:text-zinc-600 dark:hover:text-zinc-400'
            }`}
          >
            {isActive && (
              <motion.span
                layoutId={reduce ? undefined : 'top-tab-highlight'}
                transition={reduce ? { duration: 0 } : SPRING}
                aria-hidden="true"
                className="absolute inset-0 rounded-apolo-md"
                style={{
                  background: 'var(--apolo-tab-active-bg)',
                  border: '1px solid var(--apolo-tab-active-border)',
                  boxShadow: 'var(--apolo-tab-active-shadow)',
                }}
              />
            )}
            <span className="relative z-10 inline-flex items-center gap-1">
              {tab.label}
              {/* P7 — selo de maturidade: stable não tem (limpo); beta/experimental
                  avisam que a superfície pode ter arestas. Núcleo Code/Chat/Plan = limpo. */}
              {maturityBadge(tab.id) && (
                <span
                  aria-hidden="true"
                  title={maturityBadge(tab.id).title}
                  className="rounded-[4px] bg-apolo-surface2 px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide text-apolo-textTertiary"
                >
                  {maturityBadge(tab.id).label}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
